import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  HStack,
  Grid,
  GridItem,
} from '@chakra-ui/react';

// Animated SVG: Physician with stethoscope
const PhysicianSVG = () => (
  <Box as="svg" viewBox="0 0 80 80" w="72px" h="72px">
    {/* Head */}
    <circle cx="40" cy="20" r="10" fill="none" stroke="#2196f3" strokeWidth="2.5" />
    {/* Body */}
    <path
      d="M26,38 Q26,30 40,30 Q54,30 54,38 L54,52 Q54,58 48,58 L32,58 Q26,58 26,52 Z"
      fill="none"
      stroke="#2196f3"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    {/* Stethoscope */}
    <path
      d="M34,38 L34,48 Q34,54 40,54 Q46,54 46,48 L46,38"
      fill="none"
      stroke="#00bcd4"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle cx="40" cy="58" r="4" fill="none" stroke="#00bcd4" strokeWidth="2">
      <animate attributeName="r" values="4;5;4" dur="2s" repeatCount="indefinite" />
    </circle>
    <circle cx="40" cy="58" r="2" fill="#00bcd4" opacity="0.3">
      <animate attributeName="opacity" values="0.3;0.5;0.3" dur="2s" repeatCount="indefinite" />
    </circle>
    {/* Medical cross on chest */}
    <rect x="37.5" y="36" width="5" height="12" rx="1" fill="#e3f2fd" />
    <rect x="34" y="39.5" width="12" height="5" rx="1" fill="#e3f2fd" />
    {/* Pulse ring */}
    <circle cx="40" cy="58" r="8" fill="none" stroke="#00bcd4" strokeWidth="1" opacity="0">
      <animate attributeName="r" values="5;12;5" dur="2s" repeatCount="indefinite" />
      <animate attributeName="opacity" values="0.4;0;0.4" dur="2s" repeatCount="indefinite" />
    </circle>
  </Box>
);

/**
 * Physician partnership section.
 *
 * The inquiry form was removed deliberately. It posted to EmailJS, a third
 * party with no Business Associate Agreement in place, and anything a
 * visitor types into a form on a surgery center site can contain protected
 * health information. Until the BAA position is settled, inquiries go
 * through the Podium chat widget (which the practice reports is covered) or
 * the direct phone and email lines below. Nothing here collects or
 * transmits visitor data.
 */
const PhysicianPartnershipForm = () => {
  return (
    <Box
      bg="linear-gradient(135deg, #f5f7fa 0%, #e3f2fd 100%)"
      py={{ base: 16, md: 24 }}
      position="relative"
      overflow="hidden"
    >
      {/* Faded medical photo texture behind the form.
          Swap this src for a licensed stock image when one is available. */}
      <Box
        position="absolute"
        inset={0}
        bgImage="url('/facility/operating-room.jpg')"
        bgSize="cover"
        bgPosition="center"
        opacity={0.12}
        mixBlendMode="luminosity"
        pointerEvents="none"
      />
      {/* Wash so form fields stay high-contrast over the photo */}
      <Box
        position="absolute"
        inset={0}
        bg="linear-gradient(135deg, rgba(245,247,250,0.82), rgba(227,242,253,0.78))"
        pointerEvents="none"
      />

      {/* Decorative Background */}
      <Box
        position="absolute"
        top="-10%"
        left="-5%"
        w="500px"
        h="500px"
        bgGradient="linear(to-br, brand.500, starOfLife.500)"
        opacity={0.05}
        borderRadius="full"
        filter="blur(80px)"
      />
      <Box
        position="absolute"
        bottom="-10%"
        right="-5%"
        w="600px"
        h="600px"
        bgGradient="linear(to-br, starOfLife.500, brand.500)"
        opacity={0.05}
        borderRadius="full"
        filter="blur(80px)"
      />

      <Container maxW="1400px" position="relative" zIndex={1}>
        <VStack spacing={12}>
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <PhysicianSVG />
            <Heading
              size="2xl"
              bgGradient="linear(to-r, brand.500, starOfLife.500)"
              bgClip="text"
              fontWeight="800"
              lineHeight="1.4"
              pb={1}
            >
              Physician Partnership
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="3xl">
              Join our team of exceptional physicians. We're looking for dedicated specialists to partner with Lake Lucien Surgery Center.
            </Text>
          </VStack>

          {/* Two columns on desktop: form left, benefits right. Stacked on
              mobile. Keeps the section from running very long. */}
          <Grid
            templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
            gap={{ base: 8, lg: 10 }}
            w="100%"
            alignItems="start"
          >
          {/* Form Container */}
          <GridItem>
          <Box
            bg="white"
            p={{ base: 8, md: 10 }}
            borderRadius="2xl"
            boxShadow="2xl"
            w="100%"
          >
            <VStack align="stretch" spacing={6}>
              <Heading size="md" color="brand.700">
                Start the conversation
              </Heading>
              <Text color="gray.600" lineHeight="tall">
                We have moved partnership inquiries to our secure chat and
                direct lines. Message us using the chat bubble in the corner
                of this page, or reach us directly and a member of our team
                will follow up.
              </Text>

              <VStack align="stretch" spacing={3}>
                <Button
                  as="a"
                  href="tel:+13213213935"
                  size="lg"
                  bg="linear-gradient(135deg, #2196f3 0%, #00bcd4 100%)"
                  color="white"
                  _hover={{ opacity: 0.9 }}
                >
                  Call (321) 321-3935
                </Button>
                <Button
                  as="a"
                  href="mailto:info@lucienasc.com"
                  size="lg"
                  variant="outline"
                  borderColor="brand.500"
                  color="brand.600"
                  _hover={{ bg: 'brand.50' }}
                >
                  Email info@lucienasc.com
                </Button>
              </VStack>

              <Text fontSize="sm" color="gray.500">
                Please do not include personal health information in email.
              </Text>
            </VStack>
          </Box>
          </GridItem>

          {/* Benefits Section */}
          <GridItem>
          <Box
            bg="white"
            p={{ base: 8, md: 10 }}
            borderRadius="2xl"
            boxShadow="lg"
            w="100%"
          >
            <VStack align="stretch" spacing={5}>
              <Heading size="md" color="brand.700" mb={2}>
                Why Partner With Us?
              </Heading>
              
              <HStack align="start" spacing={4}>
                <Box
                  w="8px"
                  h="8px"
                  bg="starOfLife.500"
                  borderRadius="full"
                  mt={2}
                  flexShrink={0}
                />
                <Text color="gray.700">
                  <strong>State-of-the-art equipment</strong> across 19 specialties
                </Text>
              </HStack>

              <HStack align="start" spacing={4}>
                <Box
                  w="8px"
                  h="8px"
                  bg="brand.500"
                  borderRadius="full"
                  mt={2}
                  flexShrink={0}
                />
                <Text color="gray.700">
                  <strong>Physician-centric environment</strong> with data-driven operations
                </Text>
              </HStack>

              <HStack align="start" spacing={4}>
                <Box
                  w="8px"
                  h="8px"
                  bg="starOfLife.500"
                  borderRadius="full"
                  mt={2}
                  flexShrink={0}
                />
                <Text color="gray.700">
                  <strong>Advanced learning hub</strong> with conference room and inactive OR for training
                </Text>
              </HStack>

              <HStack align="start" spacing={4}>
                <Box
                  w="8px"
                  h="8px"
                  bg="brand.500"
                  borderRadius="full"
                  mt={2}
                  flexShrink={0}
                />
                <Text color="gray.700">
                  <strong>Maximized performance</strong> with OR usage rates above industry standards
                </Text>
              </HStack>
            </VStack>
          </Box>
          </GridItem>
          </Grid>
        </VStack>
      </Container>
    </Box>
  );
};

export default PhysicianPartnershipForm;
